import React, { useEffect } from 'react';
import { Image, Stack, Button } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import useChatStore, { setUserList } from 'store/chat';
import { S3_URL } from 'constants/index';
import { getCreatedDifferenceInBestUnit } from 'utils';
import { useNavigate } from 'react-router-dom';
import { useGetChatUsersQuery } from 'services';
import { isMobileOnly, isTablet, useMobileOrientation } from 'react-device-detect';
import useTranslate from 'hooks/useTranslate';
import './styles.scss';

const RecentMessages: React.FC = () => {
	const { translate } = useTranslate();
	const navigate = useNavigate();
	const { isLandscape } = useMobileOrientation();
	const userList = useChatStore((state) => state.userList);
	const { data: userListArr = [], isPending } = useGetChatUsersQuery();

	useEffect(() => {
		if (isPending) return;
		if (!Array.isArray(userListArr) || userListArr.length === 0) return;

		const existing = Array.isArray(userList) ? userList : [];
		const isSame =
			existing.length === userListArr.length &&
			JSON.stringify(existing) === JSON.stringify(userListArr);
		if (!isSame) setUserList(userListArr);
	}, [userListArr, isPending, userList]);

	const onUserSelect = (room_id: number, receiver_id: number) => {
		if (room_id && receiver_id) {
			navigate(`/chat/${receiver_id}/${room_id}`);
		}
	};

	return (
		<div className="recent-msgs white-box bg-white">
			<div className="heading d-flex align-items-center justify-content-between">
				<h5 className="fw-bold mb-0">{translate('Chat.recentMessages')}</h5>
				{userList?.length > 0 && (
					<Button
						className="transparent-btn mb-0 rounded-pill lh-1"
						onClick={() => onUserSelect(userList[0]?.roomId, userList[0]?.chatUserId)}
					>
						{translate('Button.seeMore')}
					</Button>
				)}
			</div>
			<Stack className="recent-msgs-list">
				{userList?.length > 0
					? userList.slice(0, 5)?.map((user: any, index: number) => (
							<div
								key={index}
								className="chat-profile"
								onClick={() =>
									isMobileOnly || (isTablet && !isLandscape)
										? navigate('/chat-list')
										: onUserSelect(user?.roomId, user?.chatUserId)
								}
							>
								<div className="d-flex align-items-center gap-2">
									<div className="chat-profile-img">
										<Image
											src={
												user?.profilePicture
													? S3_URL + user.profilePicture
													: IMAGE_PATHS.UserDefault
											}
											alt="profile"
											className="w-100 h-100 object-fit-cover rounded-circle"
											loading="lazy"
										/>
									</div>
									<div className="chat-profile-content flex-1">
										<div className="d-flex align-items-center justify-content-between mb-1 gap-3">
											<h6 className="fw-bold mb-0">{user?.name}</h6>
											<p className="chat-time mb-0">{getCreatedDifferenceInBestUnit(user?.time)}</p>
										</div>
										{user?.messageType !== 'text' ? (
											<div className="file-detail d-flex align-items-center gap-1">
												{user?.messageType === 'audio' && (
													<>
														<i className="ri-music-2-fill"></i>
														{translate('Header.audio')}
													</>
												)}
												{user?.messageType === 'video' && (
													<>
														<i className="ri-file-video-fill"></i> {translate('Header.video')}
													</>
												)}
												{user?.messageType === 'image' && (
													<>
														<i className="ri-image-2-fill"></i> {translate('Header.image')}
													</>
												)}
												{user?.messageType === 'doc' && (
													<>
														<i className="ri-file-fill"></i> {translate('Header.doc')}
													</>
												)}
											</div>
										) : (
											<p className="chat-msg mb-0">{user?.message}</p>
										)}
									</div>
								</div>
							</div>
						))
					: 'No Messages'}
			</Stack>
		</div>
	);
};

export default React.memo(RecentMessages);
